// src/pages/ExportChapterView.jsx
import React, { useMemo } from "react";

import awarenessData from "../content/module_awareness_areas.json";
import "../styles/exportPdf.css";

export default function ExportChapterView({
  chapterKey,
  result,
  effectivePlan,
}) {
  // 🔒 IMPORTANT:
  // Hooks must run in the same order on every render.
  // Therefore: NEVER early-return before hooks.

  // -------------------- Safe Defaults --------------------

  const safeResult = result || {};
  const chartId = safeResult?.chart_id || "UNKNOWN";

  // -------------------- Helpers --------------------

  const safe = (x) =>
    x === null || x === undefined ? "—" : String(x);

  const fmtDateTimeIST = () => {
    try {
      const d = new Date();
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      });
    } catch {
      return new Date().toLocaleString();
    }
  };

  // -------------------- Data Normalizers --------------------

  const remediesByPlanet = safeResult?.remedies?.by_planet || null;

  const planetHouseEffects = Array.isArray(safeResult?.planet_house_effects)
    ? safeResult.planet_house_effects
    : [];

  const dasha = safeResult?.dasha || null;
  

  // ✅ Hook always runs (no conditional)
  const resolvedAwareness = useMemo(() => {
    const awarenessMap = Object.fromEntries(
      (awarenessData?.areas || []).map(a => [a.id, a])
    );

    const ids =
      safeResult?.awareness?.awareness_area_ids || [];

    return Array.isArray(ids)
      ? ids.map(id => awarenessMap[id]).filter(Boolean)
      : [];
  }, [safeResult?.awareness]);

  // ✅ Hook always runs (no conditional)
  const birthChartRows = useMemo(() => {
    const planetsObj =
      safeResult?.planets && typeof safeResult.planets === "object"
        ? safeResult.planets
        : {};

    const planetHouses =
      safeResult?.planet_houses &&
      typeof safeResult.planet_houses === "object"
        ? safeResult.planet_houses
        : {};

    return Object.keys(planetsObj)
      .map((p) => {
        const pl = planetsObj[p];
        const house = planetHouses?.[p];
        return {
          planet: p,
          sign: pl?.sign || "—",
          degree:
            typeof pl?.degree_in_sign === "number"
              ? `${pl.degree_in_sign.toFixed(2)}°`
              : "—",
          house: typeof house === "number" ? `H${house}` : "—",
        };
      })
      .filter((x) => x.planet);
  }, [safeResult?.planets, safeResult?.planet_houses]);

  // 🚫 EARLY RETURN AFTER ALL HOOKS
  if (!result) return null;

  // -------------------- Small Render Helpers --------------------
  const SectionHeader = ({ title, subtitle }) => (
    <div className="sx-section-head">
      <div className="sx-section-title">{title}</div>
      {subtitle ? (
        <div className="sx-section-subtitle">{subtitle}</div>
      ) : null}
    </div>
  );

  const LockedLine = ({ label }) => (
    <div className="sx-locked">🔒 {label} is locked for this plan.</div>
  );

  const BulletList = ({ items }) => {
    const list = Array.isArray(items) ? items.filter(Boolean) : [];
    if (list.length === 0) return null;
    return (
      <ul className="sx-ul">
        {list.map((b, i) => (
          <li key={i}>{String(b)}</li>
        ))}
      </ul>
    );
  };

  const splitToBullets = (text) => {
    if (!text) return [];
    return String(text)
      .split(". ")
      .map(s => s.trim())
      .filter(Boolean)
      .slice(0, 3); // limit
  }; 

  // Sub-section heading inside a planet/remedy card
  const SubLabel = ({ children }) => (
    <div className="sx-sub-label">{children}</div>
  );

  // Dignity → CSS class helper
  const dignityClass = (dignity) => {
    const d = String(dignity || "").toLowerCase();
    if (d === "exalted") return "sx-dignity-exalted";
    if (d === "debilitated") return "sx-dignity-debilitated";
    if (d === "mooltrikona") return "sx-dignity-mooltrikona";
    return "sx-dignity-neutral";
  };

  // 🔥 ACTIVE DASHA PLANETS (DEDUPED)
  const dashaPlanets = [
    dasha?.md?.planet,
    dasha?.ad?.planet,
    dasha?.pd?.planet,
  ].filter(Boolean);

  function formatSignalTitle(id) {
    return id
      ?.toLowerCase()
      ?.replace(/_/g, " ")
      ?.replace(/\b\w/g, (l) => l.toUpperCase());
  }
  const uniquePlanets = [...new Set(dashaPlanets)];

  // 🔥 MAP ROLES (MD / AD / PD)
  const getPlanetRoles = (planet) => {
    const roles = [];

    if (dasha?.md?.planet === planet) roles.push("MD");
    if (dasha?.ad?.planet === planet) roles.push("AD");
    if (dasha?.pd?.planet === planet) roles.push("PD");

    return roles.join(" • ");
  };
  // Domain icon map for Karmic Overview
  const domainIcon = { career: "💼", wealth: "💰", relationship: "🤝", vitality: "⚡" };

  // Deduplicate remedy items by text — prevents doubling when same planet is MD & PD
  const dedupRemedyItems = (items) => {
    if (!Array.isArray(items)) return [];
    const seen = new Set();
    return items.filter((item) => {
      const text = (item?.text ?? item ?? "").toString().trim();
      if (!text || seen.has(text)) return false;
      seen.add(text);
      return true;
    });
  };

  // -------------------- Render continues below --------------------

  return (
    <div id="sx-export-root">

      {/* =========================================================
            COVER (Page-1) — Phase-13 Premium Redesign
            Chart ID is the primary identity.
            Personal data is not stored / not displayed.
          ========================================================= */}
        {chapterKey === "COVER" ? (
          <div>
            <div className="sx-cover">

              {/* ── Hero banner ─────────────────────────────────── */}
              <div className="sx-cover-hero">

                {/* Brand row: logo + plan pill */}
                <div className="sx-cover-hero-brand">
                  <div className="sx-cover-hero-logo">
                    <div className="sx-cover-hero-symbol">✦</div>
                    <div>
                      <div className="sx-cover-hero-brand-text">SysMindX Astro</div>
                      <div className="sx-cover-hero-brand-sub">Vedic Pattern Intelligence</div>
                    </div>
                  </div>
                  <div className="sx-plan-pill">{safe(effectivePlan)}</div>
                </div>

                {/* Centered title block */}
                <div className="sx-report-type-label">Vedic Karmic Awareness</div>
                <div className="sx-report-main-title">
                  Karmic Awareness<br />Report
                </div>
                <div className="sx-report-accent-line">─── ✦ ───</div>
                <div className="sx-report-version">
                  Premium Insight Report &nbsp;·&nbsp; v2.3
                </div>

              </div>

              {/* ── Chart ID — primary identity ─────────────────── */}
              <div className="sx-chart-id-card">
                <div className="sx-chart-id-label">🔑 Chart Key — Primary Identity</div>
                <div className="sx-chart-id-value">{safe(chartId)}</div>
              </div>

              {/* ── Privacy grid — personal data not stored ─────── */}
              <div className="sx-privacy-grid">
                {[
                  { label: "Name",          value: "Not Stored" },
                  { label: "Date of Birth", value: "Not Stored" },
                  { label: "Birth Time",    value: "Not Stored" },
                  { label: "Location",      value: "Not Stored" },
                ].map(({ label, value }) => (
                  <div key={label} className="sx-privacy-cell">
                    <div className="sx-privacy-cell-label">{label}</div>
                    <div className="sx-privacy-cell-value">{value}</div>
                  </div>
                ))}
              </div>

              {/* ── Generated metadata row ──────────────────────── */}
              <div className="sx-card soft" style={{ marginBottom: 14, padding: "10px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div className="sx-label">Generated On</div>
                    <div className="sx-strong" style={{ fontSize: 12 }}>
                      {fmtDateTimeIST() + " IST"}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="sx-label">Report Type</div>
                    <div className="sx-strong" style={{ fontSize: 12 }}>Karmic Awareness</div>
                  </div>
                </div>
              </div>

              <div className="sx-divider" />

              {/* ── Disclaimer + Liability ──────────────────────── */}
              <div className="sx-card soft">
                <div className="sx-strong" style={{ marginBottom: 8 }}>Disclaimer</div>
                <div>
                  This report is generated for general informational and
                  self-reflection purposes using traditional astrology-based
                  pattern interpretation. It is not a prediction or guarantee of
                  future events, and it should not be used as a substitute for
                  professional advice for medical, legal, financial, mental
                  health, or relationship decisions. For such matters, please
                  consult a qualified professional.
                </div>
                <div style={{ height: 10 }} />
                <div className="sx-strong" style={{ marginBottom: 8 }}>Liability</div>
                <div>
                  SysMindX Solutions is not responsible for decisions made based
                  on this report.
                </div>
              </div>

              <div className="sx-pill-row">
                <span className="sx-pill">Self-reflection only</span>
                <span className="sx-pill">Not prediction</span>
                <span className="sx-pill">Not professional advice</span>
                <span className="sx-pill">Privacy-first · Data not stored</span>
              </div>

              <div style={{
                marginTop: 18, fontSize: 11, color: "#94a3b8",
                textAlign: "center", borderTop: "1px solid #efefef", paddingTop: 12,
              }}>
                Self-reflection &nbsp;·&nbsp; Not prediction &nbsp;·&nbsp; Not professional advice
              </div>

            </div>
          </div>
        ) : null}

      {/* =========================================================
          BIRTH CHART
        ========================================================= */}
      {chapterKey === "BIRTH_CHART" ? (
        <div className="sx-chapter">
          <SectionHeader
            title="Birth Chart"
            subtitle="Your chart snapshot — lagna, nakshatra, and planet placements."
          />

          {!safeResult?.planets ? (
            <LockedLine label="Birth Chart" />
          ) : (
            <div style={{ display: "grid", gap: 14 }}>
              <div className="sx-dasha-current soft">

                <div style={{ marginTop: 4 }}>
                  <b>🔑 Chart Key:</b> {safe(chartId)}
                </div>
                <div style={{ marginTop: 6 }}>
                  <b>Lagna:</b> {safe(result?.lagna?.sign)}{" "}
                  {typeof safeResult?.lagna?.degree_in_sign === "number"
                    ? `${result.lagna.degree_in_sign.toFixed(2)}°`
                    : ""}
                </div>

                <div style={{ marginTop: 6 }}>
                  <b>Moon Nakshatra:</b>{" "}
                  {safe(result?.moon_nakshatra?.nakshatra)} (Pada{" "}
                  {safe(result?.moon_nakshatra?.pada)})
                </div>                
              </div>

              <div className="sx-card">
                <div className="sx-strong" style={{ marginBottom: 12 }}>
                  Planet Placements (House-wise)
                </div>

                <div className="sx-table">
                  <div className="sx-table-head">
                    <div>Planet</div>
                    <div>Sign</div>
                    <div>House</div>
                    <div>Degree</div>
                  </div>

                  {birthChartRows.length > 0 ? (
                    birthChartRows.map((r, idx) => (
                      <div key={idx} className="sx-table-row">
                        <div className="sx-bold">{safe(r.planet)}</div>
                        <div>{safe(r.sign)}</div>
                        <div>{safe(r.house)}</div>
                        <div>{safe(r.degree)}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: 12 }} className="sx-muted">
                      Planet placement data not available.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* =========================================================
            PERSONALITY TRAITS
          ========================================================= */}
        {chapterKey === "TRAITS" ? (
          <div className="sx-chapter">
            <SectionHeader
              title="Personality Traits"
              subtitle="Core tendencies, strengths, and shadow patterns."
            />

            {!safeResult?.personality_traits?.sections ? (
              <LockedLine label="Personality Traits" />
            ) : (
              <div style={{ display: "grid", gap: 14 }}>
                {result.personality_traits.sections
                  .filter((s) => s && s.ok !== false)
                  .map((s, idx) => (
                    <div key={s.id || idx} className="sx-card">

                      <div className="sx-strong" style={{ fontSize: 14 }}>
                        {safe(s.title)}
                      </div>

                      {s.subtitle ? (
                        <div
                          className="sx-muted"
                          style={{
                            marginTop: 5,
                            color: "#d4a574",
                            fontWeight: 600,
                            fontSize: 12
                          }}
                        >
                          {safe(s.subtitle)}
                        </div>
                      ) : null}

                      {s.intro ? (
                        <div style={{ marginTop: 10, color: "#374151" }}>
                          {safe(s.intro)}
                        </div>
                      ) : null}

                      <BulletList items={s.bullets} />

                      {Array.isArray(s.strengths) && s.strengths.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <SubLabel>Strengths</SubLabel>
                          <BulletList items={s.strengths} />
                        </div>
                      )}

                      {Array.isArray(s.shadow_patterns) && s.shadow_patterns.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <SubLabel>Shadow patterns</SubLabel>
                          <BulletList items={s.shadow_patterns} />
                        </div>
                      )}

                      {Array.isArray(s.karmic_awareness_guidance) &&
                        s.karmic_awareness_guidance.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <SubLabel>Awareness guidance</SubLabel>
                          <BulletList items={s.karmic_awareness_guidance} />
                        </div>
                      )}

                      {/* Optional Pada blocks */}
                      {s.pada?.number ? (
                        <div style={{ marginTop: 12 }}>
                          <SubLabel>
                            Pada {safe(s.pada.number)}: {safe(s.pada?.tone || "")}
                          </SubLabel>

                          <BulletList items={s.pada?.traits} />

                          {Array.isArray(s.pada?.shadow) && s.pada.shadow.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                              <SubLabel>Pada shadow</SubLabel>
                              <BulletList items={s.pada.shadow} />
                            </div>
                          )}

                          {s.pada?.awareness_hint ? (
                            <div style={{ marginTop: 10 }} className="sx-muted">
                              <b>Hint:</b> {safe(s.pada.awareness_hint)}
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                    </div>
                  ))}
              </div>
            )}
          </div>
        ) : null}
      
      {/* =========================================================
            PLANET IN HOUSES
          ========================================================= */}
        {chapterKey === "HOUSES" ? (
          <div className="sx-chapter">
            <SectionHeader
              title="Planet in Houses"
              subtitle="House-wise karmic experiences and integration guidance."
            />

            {!planetHouseEffects.length ? (
              <LockedLine label="Planet in Houses" />
            ) : (
              <div>
                {(() => {
                  // Sort: active Dasha planets (MD → AD → PD) first, rest in original order
                  const ROLE_ORDER = { MD: 0, AD: 1, PD: 2 };
                  const ROLE_LABEL = { MD: "Mahadasha", AD: "Antardasha", PD: "Pratyantar" };
                  const ROLE_COLOR = { MD: "#f97316", AD: "#22d3ee", PD: "#a855f7" };

                  const activePlanetRoles: Record<string, string> = {};
                  if (dasha?.md?.planet) activePlanetRoles[dasha.md.planet] = "MD";
                  if (dasha?.ad?.planet) activePlanetRoles[dasha.ad.planet] = "AD";
                  if (dasha?.pd?.planet) activePlanetRoles[dasha.pd.planet] = "PD";

                  const sorted = [...planetHouseEffects].sort((a, b) => {
                    const roleA = activePlanetRoles[a?.planet];
                    const roleB = activePlanetRoles[b?.planet];
                    const orderA = roleA !== undefined ? ROLE_ORDER[roleA] : 99;
                    const orderB = roleB !== undefined ? ROLE_ORDER[roleB] : 99;
                    return orderA - orderB;
                  });

                  return sorted.map((x, i) => {
                  const planet = safe(x?.planet);
                  const house = safe(x?.house);
                  const role = activePlanetRoles[x?.planet];

                  return (
                    <div key={`${planet}-${house}-${i}`} className="sx-planet-card" style={role ? { borderLeft: `3px solid ${ROLE_COLOR[role]}` } : {}}>
                      {/* Planet header row */}
                      <div className="sx-planet-header">
                        <div className="sx-strong" style={{ fontSize: 14, flex: 1 }}>
                          {planet} in House {house}
                        </div>
                        {role && (
                          <span style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: ROLE_COLOR[role],
                            border: `1px solid ${ROLE_COLOR[role]}`,
                            borderRadius: 4,
                            padding: "1px 6px",
                            marginRight: 6,
                          }}>
                            {ROLE_LABEL[role]} — active now
                          </span>
                        )}
                        <span className="sx-planet-badge">{planet}</span>
                        <span className="sx-planet-house-tag">H{house}</span>
                      </div>

                      {Array.isArray(x?.core_experience) &&
                        x.core_experience.length > 0 && (
                        <div>
                          <SubLabel>Core experience</SubLabel>
                          <BulletList items={x.core_experience} />
                        </div>
                      )}

                      {Array.isArray(x?.psychological_experience) &&
                        x.psychological_experience.length > 0 && (
                        <div>
                          <SubLabel>Psychological experience</SubLabel>
                          <BulletList items={x.psychological_experience} />
                        </div>
                      )}

                      {Array.isArray(x?.karmic_pressure) &&
                        x.karmic_pressure.length > 0 && (
                        <div>
                          <SubLabel>Karmic pressure</SubLabel>
                          <BulletList items={x.karmic_pressure} />
                        </div>
                      )}

                      {Array.isArray(x?.lived_scenario) &&
                        x.lived_scenario.length > 0 && (
                        <div>
                          <SubLabel>Lived scenario</SubLabel>
                          <BulletList items={x.lived_scenario} />
                        </div>
                      )}

                      {Array.isArray(x?.integration_observation) &&
                        x.integration_observation.length > 0 && (
                        <div>
                          <SubLabel>Integration observation</SubLabel>
                          <BulletList items={x.integration_observation} />
                        </div>
                      )}
                    </div>
                  );
                  });
                })()}
              </div>
            )}
          </div>
        ) : null}

      {/* =========================================================
          REPORT SNAPSHOT — static, data-driven
        ========================================================= */}
      {chapterKey === "SUMMARY" ? (
        <div className="sx-chapter">

          <SectionHeader
            title="Report Snapshot"
            subtitle="The 3 things that matter most — at a glance."
          />

          {/* ── Active Dasha Period ── */}
          <div className="sx-dasha-current sx-avoid-break" style={{ marginBottom: 16 }}>
            <SubLabel>Your Active Cycle</SubLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 8 }}>
              {[
                { label: "Mahadasha (Life chapter)", data: dasha?.md },
                { label: "Antardasha (Current theme)", data: dasha?.ad },
                { label: "Pratyantar (Day-to-day)", data: dasha?.pd },
              ].map(({ label, data }) => (
                <div key={label} style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.18)" }}>
                  <div className="sx-small sx-muted" style={{ marginBottom: 3 }}>{label}</div>
                  <div className="sx-strong" style={{ fontSize: 13 }}>
                    {data?.planet ? safe(data.planet) : "—"}
                  </div>
                  {data?.start && data?.end && (
                    <div className="sx-small sx-muted">{safe(data.start)} → {safe(data.end)}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ── Karmic Pattern ── */}
          {safeResult?.karmic_overview_summary && (
            <div className="sx-planet-card sx-avoid-break" style={{ marginBottom: 16 }}>
              <SubLabel>Your Core Karmic Pattern</SubLabel>
              <div className="sx-strong" style={{ fontSize: 14, marginTop: 6, marginBottom: 6 }}>
                {safe(safeResult.karmic_overview_summary.pattern)}
              </div>
              <div className="sx-paragraph" style={{ WebkitLineClamp: 3, overflow: "hidden", display: "-webkit-box", WebkitBoxOrient: "vertical" }}>
                {safe(safeResult.karmic_overview_summary.description)}
              </div>
            </div>
          )}

          {/* ── Life Domains at a Glance ── */}
          {safeResult?.phase_6 && (
            <div className="sx-planet-card sx-avoid-break" style={{ marginBottom: 16 }}>
              <SubLabel>Life Domains at a Glance</SubLabel>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                {["career", "wealth", "relationship", "vitality"].map((key) => {
                  const d = safeResult.phase_6[key];
                  if (!d) return null;
                  const icons = { career: "💼", wealth: "💰", relationship: "🤝", vitality: "⚡" };
                  return (
                    <div key={key} style={{ padding: "7px 10px", borderRadius: 8, background: "rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.08)" }}>
                      <div className="sx-small" style={{ fontWeight: 700, textTransform: "capitalize", marginBottom: 2 }}>
                        {icons[key]} {key}
                      </div>
                      {d.phase_key && (
                        <div className="sx-small sx-muted">{safe(d.phase_key)} phase</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Key Practices Right Now ── */}
          {(() => {
            const mdPlanet = dasha?.md?.planet;
            const adPlanet = dasha?.ad?.planet;
            const remediesByPlanet = safeResult?.remedies?.by_planet || {};
            const pairs = [mdPlanet, adPlanet]
              .filter(Boolean)
              .map((p) => ({ planet: p, primary: (remediesByPlanet[p]?.primary || []).slice(0, 2) }))
              .filter(({ primary }) => primary.length > 0);

            if (pairs.length === 0) return null;
            return (
              <div className="sx-planet-card sx-avoid-break" style={{ marginBottom: 16 }}>
                <SubLabel>Key Practices Right Now — pick 1 or 2</SubLabel>
                <div style={{ marginTop: 8, display: "grid", gap: 10 }}>
                  {pairs.map(({ planet, primary }) => (
                    <div key={planet}>
                      <div className="sx-small" style={{ fontWeight: 700, marginBottom: 4 }}>{planet}</div>
                      <ul className="sx-ul" style={{ margin: 0 }}>
                        {primary.map((item, i) => (
                          <li key={i}>{safe(item?.text ?? item)}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ── 3 Things to Notice ── */}
          {(() => {
            const signals = (safeResult?.awareness_signals || []).slice(0, 3);
            if (signals.length === 0) return null;
            return (
              <div className="sx-planet-card sx-avoid-break">
                <SubLabel>3 Patterns to Notice This Period</SubLabel>
                <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                  {signals.map((s, i) => (
                    <div key={i}>
                      <span className="sx-bold">{formatSignalTitle(s.signal_id)}</span>
                      {s.theme && <span className="sx-muted"> — {safe(s.theme)}</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

        </div>
      ) : null}

      {/* =========================================================
          AI GENERATED SUMMARY — narrative prose from /summarize
        ========================================================= */}
      {chapterKey === "AI_SUMMARY" ? (
        <div className="sx-chapter">

          <SectionHeader
            title="AI Generated Summary"
            subtitle="A narrative built entirely from your chart's own calculated data — not a generic reading."
          />

          {safeResult?.ai_summary ? (
            <div>

              {/* Framing note */}
              <div className="sx-card soft sx-avoid-break" style={{ marginBottom: 18 }}>
                <div className="sx-small sx-muted" style={{ lineHeight: 1.75 }}>
                  <b style={{ color: "#1e293b" }}>Built from your chart, not a template.</b>{" "}
                  Every sentence in this summary is drawn from your specific planetary positions,
                  active Dasha periods, domain patterns, and remedy signals — the same data
                  detailed in the chapters that follow. No two charts produce the same summary.
                  If you regenerate your chart, the AI will interpret the same underlying data
                  in a slightly different way — which is a useful way to test how well it
                  reflects what resonates for you.
                </div>
              </div>

              {/* Paragraphs — split on double newline */}
              {String(safeResult.ai_summary)
                .split(/\n\n+/)
                .filter(Boolean)
                .filter((para) => !para.trim().startsWith("#"))
                .map((para, i) => (
                  <div key={i} className="sx-paragraph sx-avoid-break" style={{ marginBottom: 14, lineHeight: 1.8, textAlign: "justify", overflowWrap: "break-word" }}>
                    {para.trim()}
                  </div>
                ))}

            </div>
          ) : (
            <div className="sx-card soft">
              <div className="sx-muted">
                AI summary is not available for this export. The /summarize endpoint may be
                offline or the API key may not be configured. The static snapshot on the
                previous page remains your primary reference.
              </div>
            </div>
          )}

        </div>
      ) : null}

      {/* =========================================================
          HOW TO USE THIS REPORT
        ========================================================= */}
      {chapterKey === "HOW_TO_USE" ? (
        <div className="sx-chapter">

          <SectionHeader
            title="How to Use This Report"
            subtitle="A short guide to getting the most from your Karmic Awareness Report."
          />

          {/* Reading path intro */}
          <div className="sx-card soft sx-avoid-break" style={{ marginBottom: 18 }}>
            <div className="sx-paragraph">
              This report is best read as a reflection tool — not a prediction.
              It surfaces patterns rooted in your birth chart so you can notice
              them clearly, not worry about them. Start from the top and follow
              the three steps below.
            </div>
          </div>

          {/* Step 1 */}
          <div className="sx-planet-card sx-avoid-break" style={{ marginBottom: 14 }}>
            <div className="sx-strong" style={{ marginBottom: 6 }}>
              Step 1 — Read Your Karmic Overview
            </div>
            <div className="sx-paragraph">
              Start with your <b>Karmic Pattern</b> and the four domain cards
              (Career, Wealth, Relationship, Vitality). These describe broad
              life themes — not events. They give you the context for everything
              that follows.
            </div>
            <div className="sx-muted" style={{ marginTop: 8 }}>
              ↳ Comes right after this page.
            </div>
          </div>

          {/* Step 2 */}
          <div className="sx-planet-card sx-avoid-break" style={{ marginBottom: 14 }}>
            <div className="sx-strong" style={{ marginBottom: 6 }}>
              Step 2 — Act on Karmic Remedies
            </div>
            <div className="sx-paragraph">
              Go to <b>Karmic Remedies</b> and start with the Primary practices
              for your active Dasha planets — Mahadasha first, then Antardasha.
              Begin with one practice and let it settle for 3–4 weeks before
              adding more. Remedies work best as quiet habits, not forced rituals.
            </div>
            <div className="sx-muted" style={{ marginTop: 8 }}>
              ↳ Mahadasha shapes the long-term chapter. Antardasha brings
              specific situations. Pratyantar shifts day-to-day intensity.
            </div>
          </div>

          {/* Step 3 */}
          <div className="sx-planet-card sx-avoid-break" style={{ marginBottom: 14 }}>
            <div className="sx-strong" style={{ marginBottom: 6 }}>
              Step 3 — Notice the Awareness Patterns
            </div>
            <div className="sx-paragraph">
              The <b>Awareness</b> section has two parts.
              The first shows active signals from your current Dasha period —
              patterns that tend to surface right now.
              The second shows lifetime awareness areas rooted in your birth chart.
              Both are observation prompts, not warnings.
            </div>
            <div className="sx-muted" style={{ marginTop: 8 }}>
              ↳ Everything else — Birth Chart, Planet in Houses, Yogas,
              Dasha details — is reference depth. Use it when you want to
              explore, not as a checklist.
            </div>
          </div>

          {/* Closing note */}
          <div className="sx-card soft sx-avoid-break">
            <div className="sx-small sx-muted" style={{ lineHeight: 1.75 }}>
              <b style={{ color: "#1e293b" }}>A note on language:</b> Words
              like "dosha" or "debilitated" appear in classical texts but are
              not used here as problems to fix. They are markers of where
              energy tends to be more sensitive — nothing more. The goal of
              this report is calm awareness, not alarm.
            </div>
          </div>

        </div>
      ) : null}

      {/* =========================================================
          KARMIC OVERVIEW
        ========================================================= */}
      {chapterKey === "KARMIC_OVERVIEW" ? (
        <div className="sx-chapter">
          <SectionHeader
            title="Karmic Overview"
            subtitle="Domains: Career, Wealth, Relationship, Vitality — awareness, not prediction."
          />

          {safeResult?.phase_6 ? (
            <div style={{ display: "grid", gap: 14 }}>
                        <SubLabel>Current Karmic Pattern</SubLabel>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginTop: 2, marginBottom: 6 }}>
                          {result?.karmic_overview_summary.pattern}
                        </div>
                      <div style={{ marginBottom: 6, color: "#374151" }}>
                        {result?.karmic_overview_summary.description}
                      </div>
              {["career", "wealth", "relationship", "vitality"].map((key) => {
                const d = result.phase_6?.[key];
                
                if (!d) return null;

                return (
                  <div key={key} className="sx-planet-card sx-avoid-break">
                    <div className="sx-domain-title">
                      <span className="sx-domain-icon">
                        {domainIcon[key] || "•"}
                      </span>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </div>

                    {d.identity_text ? (
                      <div style={{ marginTop: 6, color: "#374151" }}>
                        {safe(d.identity_text)}
                      </div>
                    ) : null}

                    {d.phase_key ? (
                      <div className="sx-muted" style={{ marginTop: 5 }}>
                        <SubLabel>{safe(d.phase_key)} PHASE</SubLabel>
                      </div>
                    ) : null}

                    {d.time_text ? (
                      <div className="sx-muted" style={{ marginTop: 5 }}>
                        {safe(d.time_text)}
                      </div>
                    ) : null}

                    {d.why_text ? (
                      <div style={{ marginTop: 10 }}>
                        <SubLabel>Why this pattern</SubLabel>
                        <div style={{ color: "#4b5563", marginTop: 3 }}>
                          {safe(d.why_text)}
                        </div>
                      </div>
                    ) : null}

                    {d.guidance ? (
                      <div style={{ marginTop: 10 }}>
                        <SubLabel>Guidance</SubLabel>
                        <div style={{ color: "#4b5563", marginTop: 3 }}>
                          {safe(d.guidance)}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="sx-muted">
              Karmic overview not available.
            </div>
          )}
        </div>
      ) : null}

        {/* =========================================================
            KARMIC REMEDIES
          ========================================================= */}
        {chapterKey === "REMEDIES" ? (
          <div className="sx-chapter">
            <SectionHeader
              title="Karmic Remedies"
              subtitle="Behavioral, charity, and spiritual balancing practices."
            />

            {!remediesByPlanet ? (
              <LockedLine label="Karmic Remedies" />
            ) : (
              <div style={{ display: "grid", gap: 18 }}>

                {/* =====================================================
                    1️⃣ DASHA CONTEXT CARD
                ===================================================== */}
                {dasha ? (
                  <div className="sx-dasha-current sx-avoid-break">
                    <SubLabel>
                      Active Period Context
                    </SubLabel>

                    <div className="sx-muted sx-small" style={{ marginBottom: 10 }}>
                      Remedies below are prioritised according to currently active
                      Dasha influences.
                    </div>

                    <div style={{ display: "grid", gap: 6 }}>
                      {dasha?.md?.planet && (
                        <div>
                          <span className="sx-bold">Mahadasha:</span>{" "}
                          {safe(dasha.md.planet)}{" "}
                          <span className="sx-muted">
                            ({safe(dasha.md.start)} → {safe(dasha.md.end)})
                          </span>
                        </div>
                      )}

                      {dasha?.ad?.planet && (
                        <div>
                          <span className="sx-bold">Antardasha:</span>{" "}
                          {safe(dasha.ad.planet)}{" "}
                          <span className="sx-muted">
                            ({safe(dasha.ad.start)} → {safe(dasha.ad.end)})
                          </span>
                        </div>
                      )}

                      {dasha?.pd?.planet && (
                        <div>
                          <span className="sx-bold">Pratyantardasha:</span>{" "}
                          {safe(dasha.pd.planet)}{" "}
                          <span className="sx-muted">
                            ({safe(dasha.pd.start)} → {safe(dasha.pd.end)})
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="sx-card soft" style={{ marginTop: 10, marginBottom: 18 }}>
                  <div className="sx-small sx-muted">
                   
                    <SubLabel>Practical flow of guidance</SubLabel>

                    <ul style={{marginTop:8, paddingLeft:4, lineHeight: 1.7}}>

                    <li>
                    Start with the <b>Mahadasha</b> — it names the long-term
                    chapter shaping your life right now.
                    </li>

                    <li>
                    The <b>Antardasha</b> narrows focus to the specific situations
                    and themes active within that chapter.
                    </li>

                    <li>
                    The <b>Pratyantardasha</b> reflects short-term intensity —
                    where small, consistent practices have the most effect.
                    </li>

                    </ul>

                    <div style={{marginTop:8, fontSize:12, color:"#94a3b8"}}>
                    Begin with Primary practices and let them settle before
                    layering in Secondary or Tertiary.
                    </div>
                  </div>
                </div>
                {/* =====================================================
                      2️⃣ ACTIVE PLANET REMEDIES (DEDUPED)
                  ===================================================== */}
                  {uniquePlanets.map((planetKey) => {
                    const r = remediesByPlanet?.[planetKey];
                    if (!r) return null;

                    const roles = getPlanetRoles(planetKey);

                    return (
                      <div key={planetKey} className="sx-planet-card sx-remedy-focus">

                        {/* Header */}
                        <div className="sx-planet-header">
                          <div className="sx-strong" style={{ fontSize: 14, flex: 1 }}>
                            {planetKey}
                          </div>

                          {/* 🔥 ROLE BADGE */}
                          <span className="sx-dasha-role">
                            {roles}
                          </span>

                          <span className="sx-planet-badge">{planetKey}</span>
                        </div>

                        {/* Gentle note when planet governs multiple tiers */}
                        {roles.includes("•") && (
                          <div className="sx-small sx-muted" style={{ marginTop: 4, marginBottom: 10, fontStyle: "italic" }}>
                            {planetKey} is active in more than one Dasha tier ({roles}) —
                            its influence is intensified right now, and its practices apply
                            across all those periods. They are shown once below.
                          </div>
                        )}

                        {/* PRIMARY */}
                        {dedupRemedyItems(r?.primary).length > 0 && (
                          <div className="sx-remedy-section">
                            <SubLabel>Primary — Behavior & Mindset</SubLabel>
                            <BulletList items={dedupRemedyItems(r.primary).map((x) => x?.text)} />
                          </div>
                        )}

                        {/* SECONDARY */}
                        {dedupRemedyItems(r?.secondary).length > 0 && (
                          <div className="sx-remedy-section">
                            <SubLabel>Secondary — Service, Charity & Actions</SubLabel>
                            <BulletList items={dedupRemedyItems(r.secondary).map((x) => x?.text)} />
                          </div>
                        )}

                        {/* TERTIARY */}
                        {dedupRemedyItems(r?.tertiary).length > 0 && (
                          <div className="sx-remedy-section">
                            <SubLabel>Tertiary — Mantras & Spiritual Support</SubLabel>
                            <BulletList items={dedupRemedyItems(r.tertiary).map((x) => x?.text)} />
                          </div>
                        )}

                      </div>
                    );
                  })}
                                </div>
                              )}
                            </div>
                          ) : null}
      {/* =========================================================
            AWARENESS
          ========================================================= */}
          {chapterKey === "AWARENESS" ? (
            <div className="sx-chapter">

              <SectionHeader
                title="Awareness"
                subtitle="Patterns worth noticing — from your current Dasha period and your birth chart."
              />

              {/* ─── PART A: Active Dasha Signals ─── */}
              <div className="sx-dasha-current sx-avoid-break" style={{ marginBottom: 22 }}>

                <SubLabel>Part A — Active Signals (This Dasha Period)</SubLabel>

                <div className="sx-muted" style={{ marginBottom: 12, marginTop: 4 }}>
                  These are patterns your active planets tend to surface during
                  this period. They are not fixed traits — they shift with each
                  Dasha cycle. Worth noticing, not worth worrying about.
                </div>

                {Array.isArray(safeResult?.awareness_signals) && safeResult.awareness_signals.length > 0 ? (
                  <div style={{ display: "grid", gap: 10 }}>
                    {safeResult.awareness_signals.slice(0, 3).map((s, i) => (
                      <div key={i} className="sx-planet-card sx-avoid-break">

                        <div className="sx-strong" style={{ marginBottom: 4 }}>
                          {formatSignalTitle(s.signal_id)}
                        </div>

                        {s.theme && (
                          <div className="sx-paragraph">
                            {safe(s.theme)}
                          </div>
                        )}

                        {Array.isArray(s.source_refs) && s.source_refs[0] && (
                          <div className="sx-small sx-muted" style={{ marginTop: 6, fontStyle: "italic" }}>
                            Active in: {safe(s.source_refs[0])} period
                          </div>
                        )}

                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="sx-muted">
                    No specific awareness signals are highlighted for this phase.
                    Continue focusing on steady routines and balanced actions.
                  </div>
                )}

              </div>

              {/* ─── PART B: Lifetime Awareness Areas ─── */}
              <div>

                <SubLabel>Part B — Lifetime Awareness Areas (Birth Chart)</SubLabel>

                <div className="sx-muted" style={{ marginBottom: 12, marginTop: 4 }}>
                  These patterns are rooted in your birth chart and remain
                  constant across your life. In classical texts they are sometimes
                  called dosha — here they are treated only as awareness markers,
                  never as problems to fix.
                </div>

                {!safeResult?.awareness?.enabled ? (
                  <LockedLine label="Awareness Areas" />
                ) : resolvedAwareness.length === 0 ? (
                  <div className="sx-muted">
                    No specific awareness patterns are highlighted for this chart.
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 14 }}>
                    {resolvedAwareness.map((a, idx) => (
                      <div
                        key={idx}
                        className="sx-planet-card sx-awareness-card"
                      >

                        <div className="sx-strong" style={{ marginBottom: 6 }}>
                          {safe(a.summary)}
                        </div>

                        {a?.details && (
                          <div className="sx-paragraph">
                            {safe(a.details)}
                          </div>
                        )}

                        {a?.system_context && (
                          <div className="sx-small sx-muted" style={{ marginTop: 6, fontStyle: "italic" }}>
                            {safe(a.system_context)}
                          </div>
                        )}

                      </div>
                    ))}
                  </div>
                )}

              </div>

            </div>
          ) : null}
      {/* =========================================================
            STRONG & WEAK PLANETS — Backend Driven (SUMMARY + DETAIL)
          ========================================================= */}
          {chapterKey === "STRENGTH" ? (
            <div className="sx-chapter">
              <SectionHeader
                title="Strong & Weak Planets"
                subtitle="Energy visibility based on dignity and house placement."
              />

              {!safeResult?.strength_detail?.enabled ? null : (
                <div style={{ display: "grid", gap: 18 }}>

                  {/* ================= MODE DETECTION ================= */}
                  {(() => {
                    const isSummary = effectivePlan === "GROWTH";

                    const distribution = safeResult?.strength_detail?.distribution || {
                      dominant: 0,
                      balanced: 0,
                      sensitive: 0
                    };

                    const planets = (safeResult?.strength_detail?.insights || [])
                      .sort((a, b) => (b.total_score || 0) - (a.total_score || 0));

                    return (
                      <>
                        {/* ================= ENERGY OVERVIEW ================= */}
                        <div className="sx-dasha-current sx-avoid-break">
                          <div className="sx-strong" style={{ marginBottom: 6 }}>
                            Energy Overview
                          </div>

                          <div className="sx-muted">
                            You have {distribution.dominant} dominant,{" "}
                            {distribution.balanced} balanced, and{" "}
                            {distribution.sensitive} subtle planetary energies.
                            These describe relative visibility — not advantage or limitation.
                          </div>
                        </div>

                        {/* ================= SUMMARY MODE ================= */}
                        {isSummary ? (
                          <div style={{ display: "grid", gap: 10 }}>
                            {planets.map((p, idx) => (
                              <div key={idx} className="sx-summary-row">

                                <div className="sx-bold">
                                  {p.planet}
                                </div>

                                <div className="sx-muted" style={{ display: "flex", gap: 8 }}>
                                  <span className={`sx-strength-pill sx-strength-${String(p.energy_type || "").toLowerCase()}`}>
                                    {p.energy_type}
                                  </span>

                                  <span className={`sx-dignity-pill ${dignityClass(p?.dignity)}`}>
                                    {p?.dignity || "—"}
                                  </span>
                                </div>

                              </div>
                            ))}
                          </div>
                        ) : (
                          /* ================= DETAIL MODE (INSIGHT) ================= */
                          <div style={{ display: "grid", gap: 18 }}>
                            {planets.map((p, idx) => {

                              const details = p?.details || {};

                              return (
                                <div
                                  key={idx}
                                  className="sx-planet-card sx-avoid-break"
                                  style={{ paddingTop: 18, paddingBottom: 18 }}
                                >
                                  {/* HEADER */}
                                  <div className="sx-planet-header">
                                    <div className="sx-strong sx-planet-name">
                                      {p.planet}
                                    </div>

                                    <div className="sx-planet-meta" style={{ display: "flex", gap: 8 }}>
                                      <span className={`sx-strength-pill sx-strength-${String(p.energy_type || "").toLowerCase()}`}>
                                        {p.energy_type}
                                      </span>

                                      <span className={`sx-dignity-pill ${dignityClass(p?.dignity)}`}>
                                        {p?.dignity || "—"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* CORE EXPRESSION */}
                                  {(details?.planetArchetype ||
                                    details?.signExpression ||
                                    details?.houseExpression) && (
                                    <div className="sx-block">
                                      <SubLabel>Core Expression</SubLabel>

                                      <div className="sx-ul">
                                        {splitToBullets(details.planetArchetype).map((b, i) => (
                                          <div key={`core-${i}`} className="sx-li">• {b}</div>
                                        ))}
                                      </div>

                                      {details?.signExpression && (
                                        <div className="sx-ul">
                                          {splitToBullets(details.signExpression).map((b, i) => (
                                            <div key={`sign-${i}`} className="sx-li">• {b}</div>
                                          ))}
                                        </div>
                                      )}

                                      {details?.houseExpression && (
                                        <div className="sx-ul">
                                          {splitToBullets(details.houseExpression).map((b, i) => (
                                            <div key={`house-${i}`} className="sx-li">• {b}</div>
                                          ))}
                                        </div>
                                      )}
                                      </div>
                                    )}
                                  

                                  {/* OPERATION */}
                                  {details?.operationalTone && (
                                    <div className="sx-block">
                                      <SubLabel>How This Energy Operates</SubLabel>
                                      <div className="sx-paragraph">
                                        {details.operationalTone}
                                      </div>
                                    </div>
                                  )}

                                  {/* AWARENESS */}
                                  {Array.isArray(details?.awarenessBullets) &&
                                    details.awarenessBullets.length > 0 && (
                                      <div className="sx-block">
                                        <SubLabel>Awareness Balance</SubLabel>
                                        <BulletList items={details.awarenessBullets} />
                                      </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : null}

     {/* =========================================================
              PLANET CONJUNCTIONS
          ========================================================= */}
          {chapterKey === "CONJUNCTIONS" ? (() => {

  const conj = safeResult?.conjunctions;
  const list = conj?.by_house || [];

  const isInsight = conj?.mode === "REAL";   // only for detail view

  // ✅ SAFE CALCULATIONS
  const totalClusters = list.length;

  const largestCluster =
    list.length > 0
      ? Math.max(...list.map(c => c?.conjunction_size || 0))
      : 0;

  const houses =
    list.length > 0
      ? list.map(c => `H-${c?.house}`).join(", ")
      : "—";

  const tightCount =
    list.filter(c => c?.interaction_strength === "tight").length;

  return (
    <div className="sx-chapter">

      <SectionHeader
        title="Planet Conjunctions"
        subtitle="Merged planetary themes intensifying life areas."
      />

      {!conj?.enabled ? (
        <LockedLine label="Planet Conjunctions" />
      ) : list.length > 0 ? (
        <>

          {/* ✅ ALWAYS SHOW — NO PLAN CONDITION */}
          <div className="sx-dasha-current">

            <div className="sx-strong" style={{ marginBottom: 8 }}>
              Conjunction Summary
            </div>

            <div className="sx-ul">
              <div className="sx-li">• Total clusters detected: {totalClusters}</div>
              <div className="sx-li">• Largest cluster: {largestCluster} planets</div>
              <div className="sx-li">• Houses involved: {houses}</div>
              <div className="sx-li">• Tight interactions (close orb): {tightCount}</div>
            </div>

            <div className="sx-paragraph sx-soft" style={{ marginTop: 8 }}>
              Conjunctions reflect blended planetary themes — not fixed outcomes.
            </div>

          </div>

          {/* ================= GROWTH (SUMMARY MODE) ================= */}
          {conj?.mode === "SUMMARY" && (
            <div style={{ display: "grid", gap: 14 }}>
              {list.map((c, idx) => (
                <div key={idx} className="sx-planet-card">

                  <div className="sx-planet-header">
                    <div className="sx-strong" style={{ fontSize: 14, flex: 1 }}>
                      House {c?.house}
                    </div>
                    <span className="sx-planet-house-tag">
                      H{c?.house}
                    </span>
                  </div>

                  <div className="sx-ul">
                    <div className="sx-li">• Planets: {c?.planets?.join(", ")}</div>
                    <div className="sx-li">• Cluster size: {c?.conjunction_size || "—"}</div>
                    <div className="sx-li">• Interaction: {c?.interaction_strength || "—"}</div>
                  </div>

                </div>
              ))}
            </div>
          )}
                        {/* ================= INSIGHT MODE ================= */}
                        {isInsight && (
                          <div style={{ display: "grid", gap: 14 }}>
                                            {list.map((c, idx) => (
                                              <div key={idx} className="sx-planet-card">

                                                {/* HEADER */}
                                                <div className="sx-planet-header">
                                                  <div className="sx-strong" style={{ fontSize: 14, flex: 1 }}>
                                                    House {c?.house}
                                                    {c?.role_label ? ` — ${c.role_label}` : ""}
                                                  </div>

                                                  <span className="sx-planet-house-tag">
                                                    H{c?.house}
                                                  </span>
                                                </div>

                                                {/* INFLUENCE */}
                                                {c?.influence_label && (
                                                  <div className="sx-block">
                                                    <div className="sx-paragraph sx-soft">
                                                      {c.influence_label}
                                                    </div>
                                                  </div>
                                                )}

                                                {/* SUMMARY */}
                                                {c?.summary && (
                                                  <div className="sx-block">
                                                    <SubLabel>Summary</SubLabel>
                                                    <div className="sx-paragraph">{c.summary}</div>
                                                  </div>
                                                )}

                                                {/* KEY EFFECTS */}
                                                {c?.key_effects?.length > 0 && (
                                                  <div className="sx-block">
                                                    <SubLabel>Key Effects</SubLabel>
                                                    <BulletList items={c.key_effects} />
                                                  </div>
                                                )}

                                                {/* CHALLENGES */}
                                                {c?.challenges?.length > 0 && (
                                                  <div className="sx-block">
                                                    <SubLabel>Challenges</SubLabel>
                                                    <BulletList items={c.challenges} />
                                                  </div>
                                                )}

                                                {/* BALANCING */}
                                                {c?.balancing_actions?.length > 0 && (
                                                  <div className="sx-block">
                                                    <SubLabel>Balancing Actions</SubLabel>
                                                    <BulletList items={c.balancing_actions} />
                                                  </div>
                                                )}

                                                {/* SYNTHESIS */}
                                                {c?.synthesis?.length > 0 && (
                                                  <div className="sx-block">
                                                    <SubLabel>How This Interaction Operates</SubLabel>

                                                    <div className="sx-ul">
                                                      {c.synthesis.map((s, i) => (
                                                        <div key={i} className="sx-li">
                                                          • {s.text}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  </div>
                                                )}

                                                {/* YOGAS */}
                                                {c?.yogas?.length > 0 && (
                                                  <div className="sx-block">
                                                    <SubLabel>Yoga Emergence</SubLabel>

                                                    {c.yogas.map((y, i) => (
                                                      <div key={i} className="sx-yoga-card">

                                                        <div className="sx-strong">{y.name}</div>

                                                        {y.summary && (
                                                          <div className="sx-paragraph">{y.summary}</div>
                                                        )}

                                                        {y.effects?.length > 0 && (
                                                          <>
                                                            <div className="sx-sub-label">Effects</div>
                                                            <BulletList items={y.effects} />
                                                          </>
                                                        )}

                                                        {y.challenges?.length > 0 && (
                                                          <>
                                                            <div className="sx-sub-label">Challenges</div>
                                                            <BulletList items={y.challenges} />
                                                          </>
                                                        )}

                                                      </div>
                                                    ))}

                                                  </div>
                                                )}

                                              </div>
                                            ))}
                                          </div>
                                        )}

                  </>
                ) : (
                  <div className="sx-muted">No conjunctions detected.</div>
                )}

              </div>
            );

          })() : null}
      
      {/* =========================================================
              YOGA ANALYSIS
          ========================================================= */}
          {chapterKey === "YOGAS" ? (() => {

            const rawYoga = result?.yoga_analysis;
            const gatedYoga = safeResult?.yoga_analysis;

            // 🔒 ALWAYS take summary from raw
            const summary = rawYoga?.summary;

            // 🔒 Normalize list for both plans
            const list =
              rawYoga?.yogas?.length
                ? rawYoga.yogas
                : gatedYoga?.insights?.length
                ? gatedYoga.insights
                : [];

            const isInsight = safeResult?.yoga_analysis?.mode === "REAL";

            return (
              <div className="sx-chapter">

                <SectionHeader
                  title="Yoga Analysis"
                  subtitle="Key yogas in the chart — interpreted as tendencies, not predictions."
                />

                {!rawYoga?.enabled ? (
                  <LockedLine label="Yoga Analysis" />
                ) : (
                  <>
                    {/* ================= SUMMARY (ALWAYS) ================= */}
                    {summary && (
                      <div className="sx-dasha-current soft">

                        <div className="sx-strong" style={{ marginBottom: 8 }}>
                          Yoga Summary
                        </div>

                        <div className="sx-ul">
                          <div className="sx-li">• Total detected: {summary.total_detected}</div>
                          <div className="sx-li">• Major yogas: {summary.major_yogas}</div>
                          <div className="sx-li">• Supportive yogas: {summary.supportive_yogas}</div>
                        </div>

                        <div className="sx-paragraph sx-soft" style={{ marginTop: 8 }}>
                          Yogas activate primarily during their ruling planet periods.
                        </div>

                      </div>
                    )}
                    {/* ================= YOGA LIST ================= */}
                    {list.length > 0 ? (
                      <div style={{ display: "grid", gap: 14 }}>

                        {list.map((y, idx) => {

                          // 🔒 Normalize Insight fields safely
                          const core = y?.core_meaning || [];
                          const activation = y?.activation_principles || [];
                          const positive = y?.positive_expression || [];
                          const challenges = y?.challenging_expression || [];
                          const guidance = y?.remedial_guidance || [];

                          return (
                            <div
                              key={idx}
                              className={isInsight ? "sx-yoga-card" : "sx-planet-card"}
                            >

                              {/* TITLE */}
                              <div className="sx-strong" style={{ fontSize: 14 }}>
                                {y?.title}
                              </div>

                              {/* ================= INSIGHT MODE ================= */}
                              {isInsight && (
                                <>

                                  {/* CORE MEANING */}
                                  {core.length > 0 && (
                                    <div className="sx-block">
                                      <SubLabel>Core Meaning</SubLabel>
                                      <div className="sx-ul">
                                        {core.map((item, i) => (
                                          <div key={i} className="sx-li">• {item}</div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* ACTIVATION */}
                                  {activation.length > 0 && (
                                    <div className="sx-block">
                                      <SubLabel>Activation Pattern</SubLabel>
                                      <BulletList items={activation} />
                                    </div>
                                  )}

                                  {/* POSITIVE */}
                                  {positive.length > 0 && (
                                    <div className="sx-block">
                                      <SubLabel>Positive Expression</SubLabel>
                                      <BulletList items={positive} />
                                    </div>
                                  )}

                                  {/* CHALLENGES */}
                                  {challenges.length > 0 && (
                                    <div className="sx-block">
                                      <SubLabel>Challenging Expression</SubLabel>
                                      <BulletList items={challenges} />
                                    </div>
                                  )}

                                  {/* GUIDANCE */}
                                  {guidance.length > 0 && (
                                    <div className="sx-block">
                                      <SubLabel>Awareness Guidance</SubLabel>
                                      <BulletList items={guidance} />
                                    </div>
                                  )}

                                </>
                              )}

                            </div>
                          );
                        })}

                      </div>
                    ) : (
                      <div className="sx-muted">No yogas detected.</div>
                    )}

                  </>
                )}

              </div>
            );

          })() : null}

      {/* =========================================================
    VIMSHOTTARI DASHA
   ========================================================= */}
{chapterKey === "DASHA" ? (
  <div className="sx-chapter">
    <SectionHeader
      title="Vimshottari Dasha"
      subtitle="Major (MD), sub (AD), and period (PD) influences."
    />

    {!dasha ? (
      <LockedLine label="Vimshottari Dasha" />
    ) : (
      <div style={{ display: "grid", gap: 18 }}>

        {/* ================= How to Read Dasha ================= */}
        <div className="sx-card soft sx-dasha-guide">
          <div className="sx-small sx-muted">
            Dasha periods indicate karmic timing windows.
            Mahadasha sets the long-term theme, Antardasha refines focus,
            and Pratyantardasha intensifies short-term experiences.
            Read this as awareness guidance — not fixed prediction.
          </div>
        </div>

        {/* ================= Current MD / AD / PD Highlight ================= */}
        {(dasha?.md || dasha?.ad || dasha?.pd) && (
          <div className="sx-dasha-current">
            <div style={{ display: "grid", gap: 6 }}>

              {dasha?.md && (
                <div>
                  <b>Current Mahadasha (MD):</b>{" "}
                  {safe(dasha?.md?.planet)} ({safe(dasha?.md?.start)} → {safe(dasha?.md?.end)})
                </div>
              )}

              {dasha?.ad && (
                <div>
                  <b>Current Antardasha (AD):</b>{" "}
                  {safe(dasha?.ad?.planet)} ({safe(dasha?.ad?.start)} → {safe(dasha?.ad?.end)})
                </div>
              )}

              {dasha?.pd && (
                <div>
                  <b>Current Pratyantardasha (PD):</b>{" "}
                  {safe(dasha?.pd?.planet)} ({safe(dasha?.pd?.start)} → {safe(dasha?.pd?.end)})
                </div>
              )}

            </div>
          </div>
        )}

        {/* ================= Mahadasha Timeline ================= */}
        {Array.isArray(dasha.md_timeline) && dasha.md_timeline.length > 0 && (
          <div className="sx-dasha-block sx-avoid-break">
            <div className="sx-dasha-title">Mahadasha Timeline</div>
            <div className="sx-dasha-subtitle">
              Long-term karmic chapters shaping major life themes.
            </div>

            <div className="sx-table">
              <div className="sx-table-head">
                <div>Planet</div>
                <div>Start</div>
                <div>End</div>
                <div></div>
              </div>

              {dasha.md_timeline.map((x, i) => (
                <div
                  key={i}
                  className={`sx-table-row${x?.is_current ? " sx-current" : ""}`}
                >
                  <div className="sx-bold">{safe(x?.planet)}</div>
                  <div>{safe(x?.start)}</div>
                  <div>{safe(x?.end)}</div>
                  <div>{x?.is_current ? "← current" : ""}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ================= Antardasha Timeline ================= */}
        {Array.isArray(dasha.ad_timeline) && dasha.ad_timeline.length > 0 && (
          <div className="sx-dasha-block sx-avoid-break">
            <div className="sx-dasha-title">Antardasha Timeline</div>
            <div className="sx-dasha-subtitle">
              Sub-period influences within the current Mahadasha.
            </div>

            <div className="sx-table">
              <div className="sx-table-head">
                <div>Planet</div>
                <div>Start</div>
                <div>End</div>
                <div></div>
              </div>

              {dasha.ad_timeline.map((x, i) => (
                <div
                  key={i}
                  className={`sx-table-row${x?.is_current ? " sx-current" : ""}`}
                >
                  <div className="sx-bold">{safe(x?.planet)}</div>
                  <div>{safe(x?.start)}</div>
                  <div>{safe(x?.end)}</div>
                  <div>{x?.is_current ? "← current" : ""}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ================= Pratyantardasha Timeline ================= */}
        {Array.isArray(dasha.pd_timeline) && dasha.pd_timeline.length > 0 && (
          <div className="sx-dasha-block sx-avoid-break">
            <div className="sx-dasha-title">Pratyantardasha Timeline</div>
            <div className="sx-dasha-subtitle">
              Short-term activation windows within the current Antardasha.
            </div>

            <div className="sx-table">
              <div className="sx-table-head">
                <div>Planet</div>
                <div>Start</div>
                <div>End</div>
                <div></div>
              </div>

              {dasha.pd_timeline.map((x, i) => (
                <div
                  key={i}
                  className={`sx-table-row${x?.is_current ? " sx-current" : ""}`}
                >
                  <div className="sx-bold">{safe(x?.planet)}</div>
                  <div>{safe(x?.start)}</div>
                  <div>{safe(x?.end)}</div>
                  <div>{x?.is_current ? "← current" : ""}</div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    )}
  </div>
) : null}
      {/* =========================================================
            GLOSSARY & RESOURCES (Last Chapter)
          ========================================================= */}
        {chapterKey === "GLOSSARY" ? (
          <div className="sx-chapter">

            {/* Header banner */}
            <div className="sx-glossary-header">
              <div className="sx-glossary-header-eyebrow">Reference Guide</div>
              <div className="sx-glossary-header-title">Glossary &amp; Resources</div>
              <div className="sx-glossary-header-sub">
                Terms, guidance, and support for your Karmic Awareness journey
              </div>
            </div>

            {/* ── Astrological Terms ──────────────────────────── */}
            <div className="sx-glossary-section">
              <div className="sx-glossary-section-title">
                Astrological Terms Used in This Report
              </div>
              {[
                { name: "Lagna (Ascendant)",  def: "Your rising sign; how you naturally appear to the world and approach new beginnings." },
                { name: "Lagna Lord",          def: "The ruling planet of your ascendant sign; a primary indicator of your life's direction and style." },
                { name: "Nakshatra",           def: "Lunar mansion — one of 27 divisions of the sky; refines sign meaning with deeper karmic nuance." },
                { name: "Pada",                def: "One of four subdivisions within a Nakshatra; refines the Moon's placement with additional karmic nuance." },
                { name: "Karmic Pattern",      def: "The dominant structural tendency in your birth chart; the recurring life theme that runs across all domains." },
                { name: "Domain",              def: "One of four life areas — Career, Wealth, Relationship, Vitality — through which karmic patterns express." },
                { name: "House",               def: "One of 12 divisions of the birth chart, each representing a specific area of life — career, family, health, and so on." },
                { name: "Dignity",             def: "A planet's relative strength based on its sign placement; shapes how clearly or subtly its energy manifests." },
                { name: "Conjunction",         def: "Two or more planets occupying the same house; intensifies that life area's energy." },
                { name: "Yoga",                def: "Specific planetary combinations that activate particular life themes, strengths, or challenges." },
                { name: "Vimshottari Dasha",   def: "Planetary cycle system showing the timing of karmic lessons and life themes." },
                { name: "Mahadasha",           def: "Major planetary period lasting 5–20 years; the dominant karmic theme of that era of life." },
                { name: "Antardasha",          def: "Sub-period (months to years) within the Mahadasha; a secondary karmic influencing lens." },
                { name: "Pratyantardasha",     def: "Minor sub-period (weeks to months); short-term activation windows for specific themes." },
              ].map((term, i) => (
                <div key={i} className="sx-glossary-term">
                  <div className="sx-glossary-term-name">{term.name}</div>
                  <div className="sx-glossary-term-def">{term.def}</div>
                </div>
              ))}
            </div>

            {/* ── How to Use This Report ──────────────────────── */}
            <div className="sx-glossary-section">
              <div className="sx-glossary-section-title">How to Use This Report</div>
              {[
                { icon: "📖", title: "Karmic Overview",          text: "Start here — read your Karmic Pattern and the four domain cards (Career, Wealth, Relationship, Vitality). These set the context for everything that follows." },
                { icon: "🌿", title: "Practices for This Cycle", text: "Go to Karmic Remedies and work through the Primary practices for your active planets — Mahadasha first, then Antardasha. Let each settle before adding more." },
                { icon: "🔍", title: "Awareness",               text: "Visit the Awareness chapter to notice the signals your active planets are surfacing — both this period's themes and your lifetime patterns." },
                { icon: "📅", title: "Your Active Cycle",       text: "Use the Vimshottari Dasha chapter to understand the timing of your current life chapter and what follows next." },
              ].map((item, i) => (
                <div key={i} className="sx-glossary-how-item">
                  <div className="sx-glossary-how-icon">{item.icon}</div>
                  <div className="sx-glossary-how-content">
                    <div className="sx-glossary-how-title">{item.title}</div>
                    <div className="sx-glossary-how-text">{item.text}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Important Reminders ─────────────────────────── */}
            <div className="sx-glossary-section">
              <div className="sx-glossary-section-title">Important Reminders</div>
              <div className="sx-card soft">
                {[
                  "This report is for self-reflection, not prediction of future events.",
                  "Astrology should not replace professional advice for medical, legal, or financial matters.",
                  "Your free will remains the most powerful influence on your life outcomes.",
                  "Return to this report as you evolve and grow — its relevance deepens with time.",
                ].map((reminder, i) => (
                  <div key={i} className="sx-glossary-reminder-item">
                    <div className="sx-glossary-reminder-dot">✦</div>
                    <div>{reminder}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── About SysMindX Astro ────────────────────────── */}
            <div className="sx-glossary-section">
              <div className="sx-glossary-section-title">About SysMindX Astro</div>
              <div className="sx-glossary-about-box">
                <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.65, marginBottom: 12 }}>
                  SysMindX Astro creates personalized self-reflection reports grounded in
                  traditional Vedic astrology. Our reports emphasize awareness over prediction,
                  supporting clients in understanding their patterns and navigating life
                  consciously.
                </div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  <div>🌐 <strong>Website:</strong> www.astro.sysmindx.com</div>
                  <div style={{ marginTop: 5 }}>📧 <strong>Support:</strong> astro@sysmindx.com</div>
                </div>
              </div>
            </div>

            {/* ── Report footer metadata ───────────────────────── */}
            <div className="sx-glossary-footer">
              <div>
                Report ID: {safe(chartId)}&nbsp;&nbsp;·&nbsp;&nbsp;Version: Premium Insight Report v2.3
              </div>
              <div>
                Generated: {fmtDateTimeIST() + " IST"}
              </div>
            </div>

          </div>
        ) : null}

    </div>
  );
}
